import { useEffect, useState } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { CurveChart } from '@/components/admin/CurveChart';
import { CurveEditor } from '@/components/admin/CurveEditor';
import { CurveActions } from '@/components/admin/CurveActions';
import { CurveHistoryDrawer } from '@/components/admin/CurveHistoryDrawer';
import { useCurveStore } from '@/stores/useCurveStore';
import { TrendingUp, History } from 'lucide-react';
import { motion } from 'framer-motion';

const PRODUCT_TYPES = [
  { value: 'generic', label: 'Generic' },
  { value: 'microloan', label: 'Microloan' },
  { value: 'retail', label: 'Retail' },
  { value: 'hp', label: 'Hire Purchase' },
  { value: 'payday', label: 'Payday' }
];

const CurveManager = () => {
  const { product, points, loading, loadCurve } = useCurveStore();
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    loadCurve('generic');
  }, []);

  const handleProductChange = (value: string) => {
    loadCurve(value);
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <TrendingUp className="h-8 w-8" />
              Product Curve Manager
            </h1>
            <p className="text-muted-foreground">
              Configure recovery curves for different product types
            </p>
          </div>
          
          <Button
            variant="outline"
            onClick={() => setShowHistory(true)}
            className="gap-2"
          >
            <History className="h-4 w-4" />
            View History
          </Button>
        </div>

        {/* Product Selector */}
        <Card>
          <CardHeader>
            <CardTitle>Select Product Type</CardTitle>
            <CardDescription>
              Choose a product type to view and edit its recovery curve
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={product} onValueChange={handleProductChange} disabled={loading}>
              <SelectTrigger className="w-[280px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRODUCT_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">
                <strong>Recovery Rate:</strong> Percentage of nominal value expected to be recovered each month.
                For example, 0.02 (2%) means you expect to collect 2% of the outstanding balance that month.
                Changes are versioned and can be reverted from history.
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Actions */}
        <CurveActions />

        {/* Main Content Grid */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Chart */}
          {points.length > 0 && (
            <CurveChart data={points} productType={product} />
          )}

          {/* Editor */}
          <CurveEditor />
        </div>
      </div>

      {/* History Drawer */}
      <CurveHistoryDrawer
        open={showHistory}
        onOpenChange={setShowHistory}
        productType={product}
      />
    </AppLayout>
  );
};

export default CurveManager;
